import mapboxgl from "mapbox-gl";
import "./SharedPopupStyle.css";

class SharedPopupManager {
  constructor() {
    this.popup = null;
    this.handlers = new Map(); // key -> { fetchFn, type: 'raster' | 'vector' }
    this.map = null;
    this._lastClickEvent = null;
    this.boundClickHandler = this._handleClick.bind(this);
  }

  init(map) {
    if (this.map === map) return;
    this.map = map;
    this.popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      className: "shared-popup",
      maxWidth: "280px",
      offset: 12,
    });
  }

  register(key, fetchFn) {
    const wasEmpty = this.handlers.size === 0;
    this.handlers.set(key, fetchFn);

    if (wasEmpty && this.map) {
      this.map.on("click", this.boundClickHandler);
      this.map.on("touchend", this.boundClickHandler);
      this.map.getCanvas().style.cursor = "pointer";
    }

    if (this.popup?.isOpen() && this._lastClickEvent) {
      this._handleClick(this._lastClickEvent);
    }
  }

  unregister(key) {
    this.handlers.delete(key);

    if (this.handlers.size === 0 && this.map) {
      this.map.off("click", this.boundClickHandler);
      this.map.off("touchend", this.boundClickHandler);
      this.map.getCanvas().style.cursor = "";
      this.popup?.remove();
      this._lastClickEvent = null;
    } else if (this.popup?.isOpen() && this._lastClickEvent) {
      this._handleClick(this._lastClickEvent);
    }
  }

  async _handleClick(e) {
    this._lastClickEvent = e;
    const { lng, lat } = e.lngLat;

    // Check if a draw feature was also clicked
    const drawLayers =
      this.map
        ?.getStyle()
        ?.layers?.filter((l) => l.id.startsWith("gl-draw"))
        ?.map((l) => l.id) || [];

    const clickedDrawFeature =
      drawLayers.length > 0
        ? this.map.queryRenderedFeatures(e.point, { layers: drawLayers })
        : [];

    // If a drawn shape is also under the click, offset the shared popup downward
    const offset = clickedDrawFeature.length > 0 ? [0, -150] : 12;

    this.popup
      .setOffset(offset)
      .setLngLat([lng, lat])
      .setHTML(
        `
      <div class="sp-loading">
        <div class="sp-spinner"></div>
        Chargement...
      </div>
    `,
      )
      .addTo(this.map);

    this.popup
      .setLngLat([lng, lat])
      .setHTML(
        `
        <div class="sp-loading">
          <div class="sp-spinner"></div>
          Chargement...
        </div>
      `,
      )
      .addTo(this.map);

    const COLORS = [
      "#63b3ed",
      "#68d391",
      "#f6ad55",
      "#b794f4",
      "#fc8181",
      "#4fd1c5",
    ];

    const results = await Promise.allSettled(
      [...this.handlers.entries()].map(async ([key, fetchFn]) => {
        const value = await fetchFn(lng, lat);
        return { key, value };
      }),
    );

    const rows = results
      .filter((r) => r.status === "fulfilled" && r.value.value != null)
      .map((r) => r.value);

    if (rows.length === 0) {
      this.popup?.remove();
      return;
    }

    const rowsHtml = rows
      .map(({ value }, i) => {
        const color = COLORS[i % COLORS.length];

        // Raster returns { label, val, unit }, vector returns { label, name }
        if (value.val !== undefined) {
          return `
            <div class="sp-row">
              <div class="sp-label">
                <div class="sp-dot" style="background:${color}; box-shadow:0 0 6px ${color}66;"></div>
                <span class="sp-label-text">${value.label}</span>
              </div>
              <div class="sp-value-wrap">
                <span class="sp-value">${value.val}</span>
                ${value.unit ? `<span class="sp-unit">${value.unit}</span>` : ""}
              </div>
            </div>`;
        } else {
          return `
            <div class="sp-row">
              <div class="sp-label">
                <div class="sp-dot" style="background:${color}; box-shadow:0 0 6px ${color}66;"></div>
                <span class="sp-label-text">${value.label}</span>
              </div>
              <div class="sp-value-wrap"> 
                <span class="sp-value">${value.name}</span>
                ${value.code ? `<span class="sp-unit">(${value.code})</span>` : ""}
              </div>
            </div>`;
        }
      })
      .join("");

    this.popup
      .setLngLat([lng, lat])
      .setHTML(`<div>${rowsHtml}</div>`)
      .addTo(this.map);
  }

  destroy() {
    this.handlers.clear();
    this.map?.off("click", this.boundClickHandler);
    this.map?.off("touchend", this.boundClickHandler);
    this.popup?.remove();
    this._lastClickEvent = null;
    this.map = null;
  }
}
// Single shared instance
export const sharedPopupManager = new SharedPopupManager();
